import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { MapPin, Edit, Truck, Tag, CheckCircle, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { promoCodesAPI } from '../../../services/api';

export const ShippingStep = ({
  user, shippingAddress, orderItems, subtotal, deliveryDate,
  showAddressEdit, setShowAddressEdit, tempAddress, setTempAddress,
  setShippingAddress, toast,
  promoCode, setPromoCode, promoResult, setPromoResult,
  promoError, setPromoError, promoLoading, setPromoLoading,
}) => {
  const totalFilters = orderItems.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await promoCodesAPI.validate(promoCode.trim(), subtotal);
      setPromoResult(result);
      toast({ title: 'Promo Applied', description: `${result.code}: -$${result.discount_amount.toFixed(2)} off` });
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
      setPromoResult(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoResult(null);
    setPromoError('');
  };

  return (
    <>
      <div className="space-y-5">
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="bg-slate-100 rounded-t-xl px-5 py-3 flex justify-between items-center">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><MapPin className="h-4 w-4 text-sky-600" /> Shipping Address</h2>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setTempAddress(shippingAddress); setShowAddressEdit(true); }} data-testid="edit-address-btn">
              <Edit className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <MapPin className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800" data-testid="shipping-name">{user?.name}</p>
                {user?.company && <p className="text-sm text-slate-500">{user.company}</p>}
                <p className="text-sm text-slate-600 mt-1" data-testid="shipping-address">{shippingAddress || 'No address set — click Edit'}</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-sky-50 rounded-lg border border-sky-100" data-testid="delivery-estimate">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="h-4 w-4 text-sky-600" />
                <p className="text-xs font-bold text-sky-800">Estimated Delivery</p>
              </div>
              <p className="text-sm text-sky-700 font-medium">
                {deliveryDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* Promo Code Section */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="bg-slate-100 rounded-t-xl px-5 py-3">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Tag className="h-4 w-4 text-teal-600" /> Promo Code</h2>
          </div>
          <div className="p-5">
            {promoResult ? (
              <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-lg px-4 py-3" data-testid="promo-applied">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-teal-600" />
                  <div>
                    <span className="font-mono font-bold text-teal-800 text-sm">{promoResult.code}</span>
                    <span className="text-sm text-teal-600 ml-2">
                      {promoResult.discount_type === 'percentage'
                        ? `${promoResult.discount_value}% off`
                        : `$${promoResult.discount_value} off`}
                    </span>
                    <span className="text-sm font-bold text-teal-700 ml-1">(-${promoResult.discount_amount.toFixed(2)})</span>
                  </div>
                </div>
                <button onClick={handleRemovePromo} className="text-teal-500 hover:text-teal-700 p-1" data-testid="remove-promo-btn">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={promoCode}
                  onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                  placeholder="Enter promo code"
                  className="font-mono uppercase flex-1"
                  onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                  data-testid="promo-code-checkout-input"
                />
                <Button
                  onClick={handleApplyPromo}
                  disabled={promoLoading || !promoCode.trim()}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 hover:border-teal-400 px-6"
                  data-testid="apply-promo-btn"
                >
                  {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            )}
            {promoError && (
              <p className="text-xs text-red-600 mt-2" data-testid="promo-error">{promoError}</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Your Cart ({totalFilters} filters)</h3>
          <div className="space-y-2">
            {orderItems.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-600">{item.size} MERV {item.merv} x{item.quantity}</span>
                <span className="font-medium text-slate-800">${(item.price * parseInt(item.quantity || 0)).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t mt-3 pt-3 flex justify-between font-bold text-slate-800">
            <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
          </div>
          {promoResult && (
            <div className="flex justify-between text-sm text-teal-600 mt-2" data-testid="promo-discount-preview">
              <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> Promo ({promoResult.code})</span>
              <span className="font-semibold">-${promoResult.discount_amount.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showAddressEdit} onOpenChange={setShowAddressEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Shipping Address</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={tempAddress} onChange={(e) => setTempAddress(e.target.value)} placeholder="Enter full shipping address" data-testid="edit-address-input" />
            </div>
            <Button onClick={() => { setShippingAddress(tempAddress); setShowAddressEdit(false); toast({ title: 'Address Updated' }); }}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500" data-testid="save-address-btn">Save Address</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
